import { defFor } from '../cards/cardHelpers';
import { TRACK_WRAP } from '../constants';

// Applies a single reward object ({kind, amount}) to a player, returning
// the updated player. Credits/income/VP tracks have no ceiling (rulebook
// p6/p16: "there is no limit... place a charge token on the 40 spot and
// simply start counting again"), so raw totals are tracked uncapped here;
// UI/display derives the 0-40 track position with `trackPosition` below.
export function applyResourceReward(player, reward) {
  if (!reward) return player;
  if (reward.kind === 'credits' || reward.kind === 'income' || reward.kind === 'vp') {
    return { ...player, [reward.kind]: player[reward.kind] + reward.amount };
  }
  return player;
}

export function trackPosition(total) {
  return ((total % TRACK_WRAP) + TRACK_WRAP) % TRACK_WRAP;
}

// Terra Proxima's own "Directional Arrows" rulebook page (p15) shows two
// distinct shapes under one heading: a straight up/down arrow ("gain...
// the card that is in the arrow's sector" - the SAME sector, not a
// neighbor) and a diagonal one ("...in the sector one space to the
// right/left" - identical to Shy Pluto/Genesis's own "Diagonal Arrows").
// Both are handled by this same function - direction: 'same' is just
// shift: 0 in disguise (delta 0 either way), spelled out explicitly
// rather than relying on that arithmetic coincidence, since a future
// reader skimming for "how does shift 0 work" shouldn't have to notice
// direction is irrelevant at that shift to understand why 'same' isn't
// handled as its own branch. See resolveArrowChain's own comment for the
// other half of what makes these two shapes "the same mechanic, two
// icons" - the crossZone flag on the arrow object itself.
function shiftSector(sector, arrow) {
  if (!arrow) return [sector];
  if (arrow.direction === 'same') return [sector];
  if (arrow.direction === 'both') {
    return [sector - arrow.shift, sector + arrow.shift].filter((s) => s >= 1 && s <= 12);
  }
  const delta = arrow.direction === 'left' ? -arrow.shift : arrow.shift;
  const shifted = sector + delta;
  return shifted >= 1 && shifted <= 12 ? [shifted] : [];
}

// A sector's station card (there's always at most one) - contributes a
// resource reward (and possibly a charge, and possibly a text-effect
// reward) to whichever player owns it, on THEIR OWN roll. Does not
// resolve arrows here (arrow targets are a caller concern - turnActions.js's
// applyHit is the real caller, via its own lookupSectorInZone, which needs
// to avoid double-granting a sector reward already claimed via another
// arrow earlier in the same chain).
export function stationRewardsForSector(player, sectorNum) {
  const slot = player.sectors[sectorNum]?.station;
  if (!slot) return { instance: null, def: null, slot: null };
  const def = defFor(slot.cardId);
  return { instance: slot, def, slot: def.station };
}

// A sector's DEPLOYED stack - every card ever placed there, still
// contributing its own printed reward once bumped off station (see
// deployCard's own comment) - so this can return several entries at once,
// unlike stationRewardsForSector above. Each is resolved fully and
// independently by the caller (turnActions.js's applyHit/resolveArrowChain),
// arrows included.
export function deployedRewardsForSector(player, sectorNum) {
  const stack = player.sectors[sectorNum]?.deployedStack || [];
  return stack.map((instance) => ({ instance, def: defFor(instance.cardId), slot: defFor(instance.cardId).deployed }));
}

export { shiftSector };
